import { Entity, LiveData } from '@toeverything/infra';

import type { DefaultServerService } from '../../cloud';
import type { DocsService } from '../../doc';
import type { GitHubStore } from '../store/github';
import type { IntegrationRefStore } from '../store/integration-ref';
import type {
  GitHubBranch,
  GitHubConfig,
  GitHubContent,
  GitHubRepository,
  GitHubUser,
} from '../type';

// The server's name for a GitHub 401 (YOD-808). It arrives UPPERCASE, the
// same as the other integrations' error names.
const RECONNECT_REQUIRED = 'GITHUB_RECONNECT_REQUIRED';

const parseErrorName = (body: string): string | undefined => {
  try {
    const name = (JSON.parse(body) as { name?: unknown }).name;
    return typeof name === 'string' ? name.toUpperCase() : undefined;
  } catch {
    return undefined;
  }
};

/**
 * GitHub rejected this device's token. The token is stored per device, so
 * another device can still be connected; reconnecting here fixes it.
 */
export class GitHubReconnectRequiredError extends Error {
  override readonly name = RECONNECT_REQUIRED;
  constructor() {
    super(
      'GitHub connection on this device has expired. Reconnect GitHub in Settings → Integrations.'
    );
  }
}

export class GitHubIntegration extends Entity {
  constructor(
    private readonly integrationRefStore: IntegrationRefStore,
    private readonly githubStore: GitHubStore,
    private readonly docsService: DocsService,
    private readonly defaultServerService: DefaultServerService
  ) {
    super();
  }

  settings$ = LiveData.from(this.githubStore.watchSetting(), undefined);

  setting$<T extends keyof GitHubConfig>(
    key: T
  ): LiveData<GitHubConfig[T] | undefined> {
    return this.settings$.selector(setting => setting?.[key]);
  }

  updateSetting<T extends keyof GitHubConfig>(key: T, value: GitHubConfig[T]) {
    this.githubStore.setSetting(key, value);
  }

  getAccessToken(): string | undefined {
    return this.githubStore.getSetting('accessToken');
  }

  connect(accessToken: string, user: GitHubUser) {
    this.githubStore.setSettings({
      accessToken,
      username: user.login,
      avatarUrl: user.avatar_url,
    });
  }

  disconnect() {
    this.githubStore.clearSettings();
  }

  isConnected(): boolean {
    return !!this.githubStore.getSetting('accessToken');
  }

  private get baseUrl(): string {
    const server = this.defaultServerService.server;
    return server?.baseUrl || '';
  }

  private async fetchApi<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      throw new Error('Not connected to GitHub');
    }

    const url = `${this.baseUrl}/api/github${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      credentials: 'include',
      body: options?.body
        ? typeof options.body === 'string'
          ? JSON.stringify({ ...JSON.parse(options.body), accessToken })
          : options.body
        : JSON.stringify({ accessToken }),
    });

    if (!response.ok) {
      const text = await response.text();
      if (parseErrorName(text) === RECONNECT_REQUIRED) {
        throw new GitHubReconnectRequiredError();
      }
      throw new Error(`GitHub API error: ${response.status} - ${text}`);
    }

    return response.json();
  }

  // ============ User Operations ============

  async getCurrentUser(): Promise<GitHubUser> {
    const result = await this.fetchApi<{ user: GitHubUser }>('/me', {
      method: 'POST',
    });
    return result.user;
  }

  // ============ Repository Operations ============

  async listRepos(
    page = 1,
    perPage = 30,
    sort: 'updated' | 'pushed' | 'full_name' = 'pushed'
  ): Promise<GitHubRepository[]> {
    const result = await this.fetchApi<{ repos: GitHubRepository[] }>(
      `/repos?page=${page}&perPage=${perPage}&sort=${sort}`,
      { method: 'POST' }
    );
    return result.repos;
  }

  async getRepository(owner: string, repo: string): Promise<GitHubRepository> {
    const result = await this.fetchApi<{ repository: GitHubRepository }>(
      `/repos/${owner}/${repo}`,
      { method: 'POST' }
    );
    return result.repository;
  }

  async listBranches(
    owner: string,
    repo: string,
    page = 1,
    perPage = 30
  ): Promise<GitHubBranch[]> {
    const result = await this.fetchApi<{ branches: GitHubBranch[] }>(
      `/repos/${owner}/${repo}/branches?page=${page}&perPage=${perPage}`,
      { method: 'POST' }
    );
    return result.branches;
  }

  // ============ Content Operations ============

  async getContents(
    owner: string,
    repo: string,
    path = '',
    ref?: string
  ): Promise<GitHubContent | GitHubContent[]> {
    const endpoint = path
      ? `/repos/${owner}/${repo}/contents/${path}`
      : `/repos/${owner}/${repo}/contents`;

    const result = await this.fetchApi<{
      contents: GitHubContent | GitHubContent[];
    }>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ ref }),
    });
    return result.contents;
  }

  async getFileContent(
    owner: string,
    repo: string,
    path: string,
    ref?: string
  ): Promise<{ content: string; sha: string }> {
    const result = await this.fetchApi<{ content: string; sha: string }>(
      `/repos/${owner}/${repo}/file`,
      {
        method: 'POST',
        body: JSON.stringify({ path, ref }),
      }
    );
    return result;
  }

  async createOrUpdateFile(
    owner: string,
    repo: string,
    path: string,
    content: string,
    message: string,
    sha?: string,
    branch?: string
  ): Promise<{ sha: string; commitSha: string; commitUrl: string }> {
    const result = await this.fetchApi<{
      sha: string;
      commitSha: string;
      commitUrl: string;
    }>(`/repos/${owner}/${repo}/file`, {
      method: 'PUT',
      body: JSON.stringify({ path, content, message, sha, branch }),
    });
    return result;
  }

  // ============ Branch Operations ============

  async createBranch(
    owner: string,
    repo: string,
    branchName: string,
    fromBranch?: string
  ): Promise<{ ref: string; sha: string }> {
    const result = await this.fetchApi<{ ref: string; sha: string }>(
      `/repos/${owner}/${repo}/branches/create`,
      {
        method: 'POST',
        body: JSON.stringify({ branchName, fromBranch }),
      }
    );
    return result;
  }

  // ============ Pull Request Operations ============

  async createPullRequest(
    owner: string,
    repo: string,
    title: string,
    head: string,
    base: string,
    body?: string
  ): Promise<{ number: number; url: string; state: string }> {
    const result = await this.fetchApi<{
      number: number;
      url: string;
      state: string;
    }>(`/repos/${owner}/${repo}/pulls`, {
      method: 'POST',
      body: JSON.stringify({ title, head, base, body }),
    });
    return result;
  }

  // ============ Integration Ref Operations ============

  async getLinkedDocs(owner: string, repo: string): Promise<string[]> {
    const refs = this.integrationRefStore.getRefsByIntegration(
      'github',
      `${owner}/${repo}`
    );
    return refs.map(ref => ref.docId);
  }

  getDocIntegrationRef(docId: string) {
    return this.integrationRefStore.getRef(docId, 'github');
  }

  async setDocIntegrationRef(
    docId: string,
    owner: string,
    repo: string,
    path: string,
    branch: string,
    sha: string
  ) {
    this.integrationRefStore.setRef(docId, 'github', `${owner}/${repo}`, {
      owner,
      repo,
      path,
      branch,
      sha,
      lastSyncedAt: new Date().toISOString(),
    });
  }

  removeDocIntegrationRef(docId: string) {
    this.integrationRefStore.removeRef(docId, 'github');
  }

  // ============ Save to GitHub Operations ============

  /**
   * Check if the file on GitHub has changed since last sync
   */
  async checkForConflicts(docId: string): Promise<{
    hasConflict: boolean;
    remoteSha?: string;
    reconnectRequired?: boolean;
  }> {
    const ref = this.getDocIntegrationRef(docId);
    if (!ref?.refMeta) {
      return { hasConflict: false };
    }

    const { owner, repo, path, branch, sha } = ref.refMeta as {
      owner: string;
      repo: string;
      path: string;
      branch: string;
      sha: string;
    };

    try {
      const { sha: remoteSha } = await this.getFileContent(
        owner,
        repo,
        path,
        branch
      );
      return {
        hasConflict: sha !== remoteSha,
        remoteSha,
      };
    } catch (error) {
      // A dead token says nothing about the file (YOD-808)
      if (error instanceof GitHubReconnectRequiredError) {
        return { hasConflict: false, reconnectRequired: true };
      }
      // File might have been deleted
      return { hasConflict: true };
    }
  }

  /**
   * Save doc content to GitHub as a direct commit
   */
  async saveToGitHub(
    docId: string,
    markdownContent: string,
    commitMessage: string
  ): Promise<{ commitUrl: string; sha: string }> {
    const ref = this.getDocIntegrationRef(docId);
    if (!ref?.refMeta) {
      throw new Error('Document is not linked to GitHub');
    }

    const { owner, repo, path, branch, sha } = ref.refMeta as {
      owner: string;
      repo: string;
      path: string;
      branch: string;
      sha: string;
    };

    console.log('[GitHub Save] Saving to GitHub:', {
      docId,
      owner,
      repo,
      path,
      branch,
      sha,
      contentLength: markdownContent?.length,
    });

    const result = await this.createOrUpdateFile(
      owner,
      repo,
      path,
      markdownContent,
      commitMessage,
      sha,
      branch
    );

    // Update the ref with new SHA
    this.integrationRefStore.setRef(docId, 'github', `${owner}/${repo}`, {
      owner,
      repo,
      path,
      branch,
      sha: result.sha,
      lastSyncedAt: new Date().toISOString(),
    });

    return {
      commitUrl: result.commitUrl,
      sha: result.sha,
    };
  }

  /**
   * Save doc content to GitHub via pull request
   */
  async saveToGitHubAsPR(
    docId: string,
    markdownContent: string,
    commitMessage: string,
    prTitle: string,
    prBody?: string
  ): Promise<{ prUrl: string; prNumber: number }> {
    const ref = this.getDocIntegrationRef(docId);
    if (!ref?.refMeta) {
      throw new Error('Document is not linked to GitHub');
    }

    const { owner, repo, path, branch } = ref.refMeta as {
      owner: string;
      repo: string;
      path: string;
      branch: string;
    };

    // Create a new branch for the PR
    const branchName = `yodev-update-${docId.slice(0, 8)}-${Date.now()}`;
    await this.createBranch(owner, repo, branchName, branch);

    // Commit the changes to the new branch
    // First get the current file sha on the base branch
    const { sha: currentSha } = await this.getFileContent(
      owner,
      repo,
      path,
      branch
    );

    await this.createOrUpdateFile(
      owner,
      repo,
      path,
      markdownContent,
      commitMessage,
      currentSha,
      branchName
    );

    // Create the pull request
    const pr = await this.createPullRequest(
      owner,
      repo,
      prTitle,
      branchName,
      branch,
      prBody
    );

    return {
      prUrl: pr.url,
      prNumber: pr.number,
    };
  }

  /**
   * Create a new file on GitHub via pull request (for docs not linked yet).
   * Returns the file's blob sha, which matches the base branch once the PR
   * is merged unchanged.
   */
  async createFileAsPR(
    docId: string,
    owner: string,
    repo: string,
    path: string,
    markdownContent: string,
    commitMessage: string,
    baseBranch: string,
    prTitle: string,
    prBody?: string
  ): Promise<{ prUrl: string; prNumber: number; sha: string }> {
    // Create a new branch for the PR
    const branchName = `yodev-add-${docId.slice(0, 8)}-${Date.now()}`;
    await this.createBranch(owner, repo, branchName, baseBranch);

    // Add the file on the new branch (no sha: the file is new)
    const file = await this.createOrUpdateFile(
      owner,
      repo,
      path,
      markdownContent,
      commitMessage,
      undefined,
      branchName
    );

    // Create the pull request
    const pr = await this.createPullRequest(
      owner,
      repo,
      prTitle,
      branchName,
      baseBranch,
      prBody
    );

    return {
      prUrl: pr.url,
      prNumber: pr.number,
      sha: file.sha,
    };
  }
}
